import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { brl } from "@/lib/format";
import { getCache, setCache } from "@/lib/cache";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, ArrowLeft, Loader2, Package, Trophy, ChevronRight } from "lucide-react";
import { MercadoriaFichas } from "@/components/MercadoriaFichas";
import { PeriodoFiltro } from "@/components/PeriodoFiltro";
import { FichasBusca } from "@/components/FichasBusca";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

export default function MaisVendidas() {
  const [data, setData] = useState(() => getCache("mais-vendidas") ?? null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [periodo, setPeriodo] = useState({ tipo: "tudo", de: "", ate: "" });
  const navigate = useNavigate();

  useEffect(() => {
    const { de, ate } = periodo;
    api.get("/dashboard/mais-vendidas", { params: { de, ate } })
      .then((r) => { setData(r.data); if (!de && !ate) { setCache("mais-vendidas", r.data); } })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [periodo]);

  if (!data) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></AdminLayout>;

  const termo = norm(q);
  const itens = termo ? data.itens.filter((it) => norm(it.descricao).includes(termo)) : data.itens;
  const medal = ["text-yellow-400", "text-slate-300", "text-amber-600"];

  return (
    <AdminLayout>
      <button onClick={() => navigate("/painel")} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-3 transition-colors" data-testid="mv-voltar"><ArrowLeft className="w-4 h-4" /> Painel</button>
      <h1 className="font-display text-3xl sm:text-4xl font-black text-foreground mb-1">Mais vendidas</h1>
      <p className="text-muted-foreground mb-6">Ranking das mercadorias mais vendidas da sua loja. Toque em uma para ver as fichas.</p>

      <div className="relative mb-6">
        <Search className="w-5 h-5 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
        <Input data-testid="search-mais-vendidas" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar mercadoria..." className="pl-12 rounded-full bg-card" style={{ height: 52 }} />
      </div>

      <PeriodoFiltro periodo={periodo} onChange={setPeriodo} />

      {termo.length >= 2 && <FichasBusca busca={q.trim()} de={periodo.de} ate={periodo.ate} />}
      {termo.length >= 2 && itens.length > 0 && <h2 className="font-display text-lg font-bold text-foreground mb-3">Mercadorias</h2>}

      {itens.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-12 text-center">
          <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">{q ? `Nenhuma mercadoria encontrada para "${q}".` : periodo.de || periodo.ate ? "Nenhuma mercadoria vendida nesse período." : "Nenhuma mercadoria vendida ainda."}</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="mais-vendidas-list">
          {itens.map((it, i) => {
            const rank = data.itens.indexOf(it);
            return (
              <button key={it.descricao} data-testid={`mv-item-${i}`} onClick={() => setSel(it.descricao)}
                className={`w-full text-left flex items-center justify-between px-5 py-4 hover:bg-secondary/40 transition-colors ${i !== itens.length - 1 ? "border-b border-border" : ""}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-9 flex justify-center shrink-0">
                    {rank < 3 ? <Trophy className={`w-6 h-6 ${medal[rank]}`} /> : <span className="font-display font-bold text-muted-foreground">{rank + 1}º</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate">{it.descricao}</p>
                    <p className="text-sm text-muted-foreground truncate">{it.quantidade} un. · {it.vendas} venda{it.vendas > 1 ? "s" : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <p className="font-display font-bold text-primary">{brl(it.total)}</p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </button>
            );
          })}
        </div>
      )}
      <MercadoriaFichas descricao={sel} onClose={() => setSel(null)} initialDe={periodo.de} initialAte={periodo.ate} />
    </AdminLayout>
  );
}
